import { infiniteQueryOptions, keepPreviousData, queryOptions, skipToken } from '@tanstack/react-query';

import { fetchCatalogue, fetchGrantAdminTree } from '../api/catalogue.api';
import { fetchPermissionTemplates, fetchRoleTemplates } from '../api/templates.api';
import { dryRunOverrides, fetchAccessPreview, fetchAssignable, fetchInvite, fetchPeople, fetchPerson } from '../api/access.api';
import { fetchClusters, fetchLicences, previewLicence } from '../api/plants.api';
import { shouldRetry } from '../api/http';
import type { QueuedChange } from '../domain/licenceQueue';
import { fetchAudit, fetchAuditPlants } from '../api/audit.api';

import type { AuditChangeType, AuditFilters, OverrideChange } from '../types/domain.types';

import {
  normalizeAccessPreview,
  normalizeAssignable,
  normalizeCatalogue,
  normalizeDryRun,
  normalizeGrantAdminTree,
  normalizePersonAccess,
} from './normalize';
import { UC_GC, UC_STALE, userCenterKeys } from './query-keys';
import type { PeopleListFilters } from './query-keys';

export const catalogueQueryOptions = () =>
  queryOptions({
    queryKey: userCenterKeys.catalogue(),
    queryFn: ({ signal }) => fetchCatalogue(signal),
    select: normalizeCatalogue,
    staleTime: UC_STALE.catalogue,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const templatesQueryOptions = () =>
  queryOptions({
    queryKey: userCenterKeys.templates(),
    queryFn: ({ signal }) => fetchRoleTemplates(signal),
    staleTime: UC_STALE.templates,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const assignableQueryOptions = () =>
  queryOptions({
    queryKey: userCenterKeys.assignable(),
    queryFn: ({ signal }) => fetchAssignable(signal),
    select: normalizeAssignable,
    staleTime: UC_STALE.assignable,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const clustersQueryOptions = (q?: string) =>
  queryOptions({
    queryKey: userCenterKeys.clusters(q),
    queryFn: ({ signal }) => fetchClusters({ q, signal }),
    staleTime: UC_STALE.clusters,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const grantAdminTreeQueryOptions = (grant: string | undefined) =>
  queryOptions({
    queryKey: userCenterKeys.grantTree(grant ?? ''),
    queryFn: grant ? ({ signal }) => fetchGrantAdminTree(grant, signal) : skipToken,
    select: normalizeGrantAdminTree,
    staleTime: UC_STALE.catalogue,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const permissionTemplatesQueryOptions = () =>
  queryOptions({
    queryKey: userCenterKeys.permissionTemplates(),
    queryFn: ({ signal }) => fetchPermissionTemplates(signal),
    staleTime: UC_STALE.templates,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const PEOPLE_PAGE_SIZE = 50;

interface PageReply {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
}

const nextPageOf = (last: PageReply): number | undefined => (last.page * last.limit < last.total ? last.page + 1 : undefined);

export const peopleInfiniteQueryOptions = (filters: PeopleListFilters) =>
  infiniteQueryOptions({
    queryKey: userCenterKeys.people.infinite(filters),
    queryFn: ({ signal, pageParam }) => fetchPeople({ ...filters, page: pageParam, limit: PEOPLE_PAGE_SIZE }, signal),
    initialPageParam: 1,
    getNextPageParam: nextPageOf,
    staleTime: UC_STALE.people,
    gcTime: UC_GC.default,
    retry: shouldRetry,
    placeholderData: keepPreviousData,
  });

export const personQueryOptions = (userId: string | undefined) =>
  queryOptions({
    queryKey: userCenterKeys.people.detail(userId ?? ''),
    queryFn: userId ? ({ signal }) => fetchPerson(userId, signal) : skipToken,
    select: normalizePersonAccess,
    staleTime: UC_STALE.person,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const inviteQueryOptions = (userId: string | undefined) =>
  queryOptions({
    queryKey: userCenterKeys.people.invite(userId ?? ''),
    queryFn: userId ? ({ signal }) => fetchInvite(userId, signal) : skipToken,
    staleTime: UC_STALE.person,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const accessPreviewQueryOptions = (userId: string, plantId: string | undefined) =>
  queryOptions({
    queryKey: userCenterKeys.people.preview(userId, plantId ?? ''),
    queryFn: plantId ? ({ signal }) => fetchAccessPreview(userId, plantId, signal) : skipToken,
    select: normalizeAccessPreview,
    staleTime: UC_STALE.preview,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export interface DryRunTarget {
  readonly userId: string;
  readonly plantId: string;
  readonly version: number;
  readonly changes: readonly OverrideChange[];
}

export const overridesDryRunQueryOptions = ({ userId, plantId, version, changes }: DryRunTarget) =>
  queryOptions({
    queryKey: userCenterKeys.dryRun(userId, plantId, version, changes),
    queryFn: changes.length > 0 ? ({ signal }) => dryRunOverrides(userId, { plantId, changes }, signal) : skipToken,
    select: normalizeDryRun,
    staleTime: UC_STALE.dryRun,
    gcTime: UC_GC.dryRun,
    retry: shouldRetry,
    placeholderData: keepPreviousData,
  });

export const licencesQueryOptions = (plantIds?: string[]) =>
  queryOptions({
    queryKey: userCenterKeys.plants.licences(plantIds),
    queryFn: ({ signal }) => fetchLicences(plantIds, signal),
    staleTime: UC_STALE.licences,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const licencePreviewQueryOptions = (plantId: string, change: QueuedChange | undefined) => {
  const moduleKey = change?.moduleKey ?? '';
  const licensed = change?.licensed ?? false;
  return queryOptions({
    queryKey: userCenterKeys.plants.licencePreview(plantId, moduleKey, licensed),
    queryFn: change ? ({ signal }) => previewLicence(plantId, { moduleKey, licensed }, signal) : skipToken,
    staleTime: UC_STALE.licences,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });
};

export const AUDIT_PAGE_SIZE = 20;

export const auditInfiniteQueryOptions = (filters: Omit<AuditFilters, 'cursor' | 'limit'>) =>
  infiniteQueryOptions({
    queryKey: userCenterKeys.auditInfinite(filters),
    queryFn: ({ signal, pageParam }) => fetchAudit({ ...filters, cursor: pageParam ?? undefined, limit: AUDIT_PAGE_SIZE }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: UC_STALE.audit,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });

export const auditPlantsQueryOptions = (changeType: AuditChangeType) =>
  queryOptions({
    queryKey: userCenterKeys.auditPlants(changeType),
    queryFn: ({ signal }) => fetchAuditPlants({ changeType }, signal),
    staleTime: UC_STALE.audit,
    gcTime: UC_GC.default,
    retry: shouldRetry,
  });
